import { useCallback, useMemo, useState } from 'react';
import { Grid, Typography } from 'antd';
import { WizardContainer } from '../../../components/wizard/Wizard';
import { WizardProvider } from '../../../components/wizard/useWizard';
import { useTranslation } from 'react-i18next';
import { AvailabilityAfterProgramStep } from './AvailabilityAfterProgramStep';
import { CharacterAndHabitsStep } from './CharacterAndHabitsStep';
import { InspirationAndWillingnessStep } from './InspirationAndWillingnessStep';
import { PracticeStep } from './PracticeStep';
import { PreceptorRecommendationStepContainer } from './PreceptorRecommendationCollectionStep';
import { SupportStep } from './SupportStep';
import { UnderstandingOfHeartfulnessStep } from './UnderstandingOfHeartfulnessStep';
import {
	PreceptorInterviewProvider,
	usePreceptorInterview,
} from './usePreceptorInterviewWizard';
import { useInterviewLandingPage } from '../InterviewLandingPage/useInterviewLandingPage';
import {
	INTERVIEW_LANDING,
	PRECEPTOR_CONTEXT,
	VIEW_PRECEPTOR_INTERVIEW,
} from '../../../shared/Routes';
import { DietaryHabitsAndHealthStep } from './DietaryHabitsAndHealthStep';
import Icon from '../../../components/ui-components/Icon';
import LeftArrow from '../../../assets/images/arrow-left.svg';
import { UserRole } from '../../../shared/VolunteerApplicationServiceTypes';
import { useLocation } from 'react-router-dom';
import useUserSession from '../../../hooks/useUserSession';
import { SELECTED_ROLES } from '../../../shared/Utils';

const { Text } = Typography;
const { useBreakpoint } = Grid;

export const PreceptorInterviewWizard = () => {
	const { t } = useTranslation();
	const { onSubmitClick, application } = usePreceptorInterview();
	const { navigateToPage } = useInterviewLandingPage();
	const screens = useBreakpoint();
	const isMobile = !screens.sm;
	const { selectedRole } = useUserSession();
	const { pathname } = useLocation();
	const isRoleAllowed = SELECTED_ROLES.includes(selectedRole as UserRole);

	const isProposer = selectedRole === UserRole.PROPOSER;
	const isViewable = pathname.includes(VIEW_PRECEPTOR_INTERVIEW);
	const isProposerAndViewable = isProposer && isViewable;
	const currentWizard = 0;
	const stepOffset = 0;

	const wizards = useMemo(
		() => [
			{
				steps: [
					<InspirationAndWillingnessStep key={1} />,
					<PracticeStep key={2} />,
					<CharacterAndHabitsStep key={3} />,
					<AvailabilityAfterProgramStep key={4} />,
					<SupportStep key={5} />,
					<UnderstandingOfHeartfulnessStep key={6} />,
					<DietaryHabitsAndHealthStep key={7} />,
					<PreceptorRecommendationStepContainer key={8} />,
				],
				descriptions: [
					{
						title: t('inspirationAndWillingness'),
						description: t('primaryQualificationForATrainer') ?? '',
					},
					{
						title: t('practice'),
						description: t('trainerNeedsToBeIntensePractitioner') ?? '',
					},
					{
						title: t('characterAndHabits'),
						description: t('expectedToBeRoleModel') ?? '',
					},
					{
						title: t('availabilityAfterProgram'),
						description: t('expectedToBeRoleModel') ?? '',
					},
					{
						title: t('familySupport'),
						description: t('exploreIfConditionsAroundAreConducive') ?? '',
					},
					{
						title: t('understandingHeartfulness'),
						description: t('abilityToExplainHeartfulness') ?? '',
					},
					{
						title: t('dietaryHabitsAndHealth'),
						description: t('expectedToBeRoleModel') ?? '',
					},
					{
						title: t('preceptorRecommendation'),
						description: t('preceptorRecommendation') ?? '',
					},
				],
			},
		],
		[t]
	);
	const steps = wizards[currentWizard].steps;
	const descriptions = wizards[currentWizard].descriptions;
	const [stepStates, setStepStates] = useState<number[]>(() => {
		const draftIndex = 0;
		return new Array(wizards.length)
			.fill(0)
			.map((_, index) =>
				index === currentWizard ? Math.max(0, draftIndex) : 0
			);
	});

	const showModal = useCallback(async (): Promise<boolean> => {
		return Promise.resolve(true);
	}, []);

	const customHandlePrevious = useCallback(() => {
		if (stepStates[currentWizard] === 0) {
			navigateToPage(isViewable ? INTERVIEW_LANDING : PRECEPTOR_CONTEXT);
		}
	}, [stepStates, navigateToPage, isViewable]);

	const completedIndex = application?.preceptorInterviewDone ? steps.length : 0;

	const handleEvaluateClick = useCallback(() => {
		navigateToPage(INTERVIEW_LANDING);
	}, [navigateToPage]);

	return (
		<div
			className={`flex flex-col h-full min-h-0 p-[8px] ${
				!isMobile ? 'w-fit my-0 mx-auto' : 'w-full my-0 mx-auto'
			}`}
		>
			<div className="relative flex justify-between items-center w-full">
				<div
					className="flex font-sans justify-center gap-1 mb-1 items-center cursor-pointer absolute left-0"
					onClick={handleEvaluateClick}
				>
					<Icon src={LeftArrow} width={20} height={20} />
					{!isMobile && (
						<Text className="font-bold text-sm text-primary-blue whitespace-nowrap">
							{t('backToEvaluate')}
						</Text>
					)}
				</div>
				<Text className="flex-grow mt-4 mb-6 font-sans font-bold text-sm text-center text-secondary-black px-10">
					{t('candidatePreceptorInterview')}
					{isViewable && ` (${t('viewOnly')})`}
				</Text>
			</div>
			<WizardContainer
				onSubmitClick={
					isRoleAllowed ? () => void handleEvaluateClick() : onSubmitClick
				}
				onNextClick={showModal}
				onPreviousClick={customHandlePrevious}
				steps={steps}
				stepDescription={descriptions}
				stepOffset={stepOffset}
				initialStepState={stepStates[currentWizard] || 0}
				onStepStateChange={(newState: number) => {
					setStepStates((prev) => {
						const updated = [...prev];
						updated[currentWizard] = newState;
						return updated;
					});
				}}
				currentWizard={currentWizard}
				draftIndex={completedIndex}
				completedDraftIndex={completedIndex}
				isProposerAndViewable={isProposerAndViewable}
			/>
		</div>
	);
};

export const PreceptorInterviewWizardContainer = () => {
	return (
		<PreceptorInterviewProvider>
			<WizardProvider>
				<PreceptorInterviewWizard />
			</WizardProvider>
		</PreceptorInterviewProvider>
	);
};
